import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { addToastAlert, resetToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';
import { steps } from '../ampol-cash-order-modal';

/**
 * Custom hook for managing validation and delivery errors
 *
 * Handles:
 * - Delivery option validation
 * - Error message display for different delivery options
 * - Toast alert management
 *
 * @param deliveryOption - Current delivery option from Redux
 * @param isDeliveryOptionsValid - Validation state
 * @param setIsDeliveryOptionsValid - Setter for validation state
 * @returns Validation handlers
 */
export const useAmpolCashOrderValidation = (deliveryOption, isDeliveryOptionsValid, setIsDeliveryOptionsValid) => {
  const dispatch = useDispatch();

  /**
   * Shows appropriate error message based on delivery option and step
   */
  const showDeliveryErrors = useCallback(
    (option, stepId) => {
      if (option === 'optionOne' && stepId === steps.DeliveryOptions) {
        dispatch(addToastAlert(Alert.DELIVERY_INVALID_EMAIL, 'error', true));
      } else if (option === 'optionThree' && stepId === steps.DeliveryOptions) {
        dispatch(addToastAlert(Alert.DELIVERY_OPTIONS_INFO, 'neutral', true));
      }
    },
    [dispatch]
  );

  /**
   * Handles delivery option changes and updates validation state
   */
  const onDeliveryOptionsChange = useCallback(
    (option, isValid, stepId) => {
      setIsDeliveryOptionsValid(isValid);

      if (isValid || option === 'optionTwo') {
        dispatch(resetToastAlert());
      } else {
        showDeliveryErrors(option, stepId);
      }
    },
    [dispatch, setIsDeliveryOptionsValid, showDeliveryErrors]
  );

  /**
   * Handles continue button click with validation
   */
  const onDeliveryOptionsContinue = useCallback(
    (isValid, onNextClick) => {
      if (isValid) {
        onNextClick();
      } else {
        showDeliveryErrors(deliveryOption, steps.DeliveryOptions);
      }
    },
    [deliveryOption, showDeliveryErrors]
  );

  return {
    showDeliveryErrors,
    onDeliveryOptionsChange,
    onDeliveryOptionsContinue,
  };
};
